import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Stethoscope, Phone, MapPin, Calendar, ArrowLeft, Trash2, CheckCircle2, Clock, XCircle, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  new:        { text: "Nueva",       className: "bg-amber-100 text-amber-900" },
  contacted:  { text: "Contactada",  className: "bg-blue-100 text-blue-900" },
  scheduled:  { text: "Agendada",    className: "bg-indigo-100 text-indigo-900" },
  completed:  { text: "Completada",  className: "bg-emerald-100 text-emerald-900" },
  cancelled:  { text: "Cancelada",   className: "bg-rose-100 text-rose-900" },
};

const URGENCY_LABELS = {
  urgente: { text: "URGENTE", className: "bg-rose-600 text-white" },
  pronto:  { text: "Pronto",  className: "bg-amber-500 text-white" },
  normal:  { text: "Normal",  className: "bg-slate-500 text-white" },
};

export default function AdminServiceRequestsPage() {
  const [tab, setTab] = useState("new");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/service-requests", { params: { status: tab } });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.post(`/admin/service-requests/${id}/status`, { status });
      toast.success("Estado actualizado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!confirm("¿Eliminar esta solicitud?")) return;
    try { await api.delete(`/admin/service-requests/${id}`); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center text-blue-700 hover:text-blue-900 text-sm font-medium mb-3" data-testid="admin-services-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al panel
        </Link>
        <div className="flex items-start gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-blue-700 text-white grid place-items-center shadow">
            <Stethoscope className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Solicitudes de enfermería</h1>
            <p className="text-slate-600 mt-1 text-base">Gestiona las solicitudes de servicio recibidas.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-xl flex-wrap" data-testid="admin-services-tabs">
            <TabsTrigger value="new" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Nuevas</TabsTrigger>
            <TabsTrigger value="contacted" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Contactadas</TabsTrigger>
            <TabsTrigger value="scheduled" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Agendadas</TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Completadas</TabsTrigger>
            <TabsTrigger value="cancelled" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Canceladas</TabsTrigger>
            <TabsTrigger value="todos" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2">Todas</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="text-center text-slate-500 py-10">Cargando…</div>
          ) : items.length === 0 ? (
            <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="admin-services-empty">
              <p className="text-slate-600">Sin solicitudes en esta categoría.</p>
            </Card>
          ) : (
            items.map((r) => {
              const wa = r.phone.replace(/[^\d]/g, "");
              const waLink = `https://wa.me/${wa}?text=${encodeURIComponent(`Hola ${r.full_name}, somos Ciudad Enfermería. Recibimos tu solicitud de "${r.service_type}".`)}`;
              return (
                <Card key={r.id} className="p-4 rounded-2xl border border-slate-200 bg-white" data-testid={`service-row-${r.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-semibold text-base text-slate-900">{r.full_name}</h3>
                        <Badge className={`${URGENCY_LABELS[r.urgency]?.className} hover:${URGENCY_LABELS[r.urgency]?.className}`}>
                          {URGENCY_LABELS[r.urgency]?.text || r.urgency}
                        </Badge>
                        <Badge className={`${STATUS_LABELS[r.status]?.className} hover:${STATUS_LABELS[r.status]?.className}`}>
                          {STATUS_LABELS[r.status]?.text || r.status}
                        </Badge>
                      </div>
                      <div className="mt-2 text-sm text-slate-700 font-medium">{r.service_type}</div>
                      <div className="mt-2 grid sm:grid-cols-2 gap-1.5 text-xs text-slate-600">
                        <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-blue-600"/>{r.phone}</div>
                        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-blue-600"/>{r.zone}{r.address ? ` — ${r.address}` : ""}</div>
                        {r.preferred_date && <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-blue-600"/>Preferida: {r.preferred_date}</div>}
                        <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-600"/>{new Date(r.created_at).toLocaleString("es-MX")}</div>
                      </div>
                      {r.notes && <p className="mt-2 text-sm text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-2 border border-slate-100">{r.notes}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid={`service-wa-${r.id}`}>
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                          <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                        </Button>
                      </a>
                      {r.status !== "contacted" && <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "contacted")} data-testid={`service-contact-${r.id}`}>Contactada</Button>}
                      {r.status !== "scheduled" && <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "scheduled")} data-testid={`service-schedule-${r.id}`}>Agendar</Button>}
                      {r.status !== "completed" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(r.id, "completed")} data-testid={`service-complete-${r.id}`}><CheckCircle2 className="w-4 h-4 mr-1"/>Completar</Button>}
                      {r.status !== "cancelled" && <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => updateStatus(r.id, "cancelled")} data-testid={`service-cancel-${r.id}`}><XCircle className="w-4 h-4 mr-1"/>Cancelar</Button>}
                      <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => remove(r.id)} data-testid={`service-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
